/**
 * Home's project pill: which project a new chat starts in. A search box, "No
 * project", the projects, and "New project" — which, as in the prototype,
 * makes one and opens it, since a project wants its name and instructions
 * before its first chat.
 *
 * On a phone the pill is its folder alone, with the project's colour on it
 * once one is picked.
 */

import { useRef, useState } from 'react';
import { createProject } from '../../api';
import { useRouter } from '../../router';
import { useApp } from '../../state';
import { Menu, MenuDivider, MenuItem, PROJECT_COLOURS, Ph, useDismiss } from '../ui';

export function ProjectPill({
  value,
  onChange,
  open,
  onToggle,
  onClose,
  iconOnly = false,
  touch = false
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  iconOnly?: boolean;
  /** The search box waits for a tap rather than raising the keyboard. */
  touch?: boolean;
}) {
  const { projects, refreshLists } = useApp();
  const { navigate } = useRouter();
  const ref = useRef<HTMLSpanElement>(null);
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  useDismiss(ref, open, onClose);

  const selected = projects.find((p) => p.id === value) ?? null;
  const options = [
    { id: null, name: 'No project', dot: 'var(--border-strong)' },
    ...projects.map((p) => ({ id: p.id, name: p.name, dot: PROJECT_COLOURS[p.colour]?.dot ?? 'var(--primary)' }))
  ].filter((p) => p.name.toLowerCase().includes(q.trim().toLowerCase()));

  const newProject = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const project = await createProject({});
      await refreshLists();
      onClose();
      navigate(`/project/${project.id}`);
    } catch {
      // Nothing was made; the menu stays open to try again.
    } finally {
      setCreating(false);
    }
  };

  return (
    <span ref={ref} style={{ position: 'relative', display: 'flex', minWidth: 0 }}>
      <button
        type="button"
        className="bare hov-tint"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={iconOnly ? selected?.name || 'Project' : undefined}
        onClick={() => {
          setQ('');
          onToggle();
        }}
        style={
          iconOnly
            ? {
                width: 36,
                height: 36,
                flex: 'none',
                position: 'relative',
                display: 'grid',
                placeItems: 'center',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-muted)'
              }
            : {
                height: 30,
                minWidth: 0,
                maxWidth: 180,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '0 10px',
                borderRadius: 999,
                border: '1px solid var(--border-strong)',
                fontSize: 12.5,
                color: 'var(--text-muted)',
                whiteSpace: 'nowrap'
              }
        }
      >
        {iconOnly ? (
          <>
            <Ph name="folder-simple" size={18} />
            {selected ? (
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: 7,
                  right: 7,
                  width: 8,
                  height: 8,
                  borderRadius: 3,
                  background: PROJECT_COLOURS[selected.colour]?.dot ?? 'var(--primary)',
                  boxShadow: '0 0 0 2px var(--surface-card)'
                }}
              />
            ) : null}
          </>
        ) : (
          <>
            <Ph name="folder-simple" size={14} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{selected?.name ?? 'No project'}</span>
          </>
        )}
      </button>

      {open ? (
        <Menu style={{ left: 0, bottom: 'calc(100% + 6px)', width: 260, maxWidth: 'calc(100vw - 32px)' }}>
          <div
            style={{
              height: 32,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '0 9px',
              marginBottom: 4,
              borderRadius: 6,
              background: 'var(--app-panel-sunken)'
            }}
          >
            <Ph name="magnifying-glass" size={14} color="var(--text-faint)" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Find a project"
              aria-label="Find a project"
              autoFocus={!touch}
              style={{
                flex: 1,
                minWidth: 0,
                border: 0,
                background: 'transparent',
                fontSize: 13,
                color: 'var(--text-strong)',
                boxShadow: 'none'
              }}
            />
          </div>
          <div className="scroll" style={{ maxHeight: 220, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            {options.map((p) => (
              <MenuItem
                key={p.id ?? 'none'}
                label={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 8, height: 8, flex: 'none', borderRadius: 3, background: p.dot }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                  </span>
                }
                selected={p.id === value}
                onSelect={() => {
                  onChange(p.id);
                  onClose();
                }}
                style={{ flex: 'none', background: 'transparent' }}
              />
            ))}
          </div>
          <MenuDivider />
          <MenuItem
            icon={creating ? 'circle-notch' : 'plus'}
            label="New project"
            onSelect={() => void newProject()}
            style={{ color: 'var(--text-muted)' }}
          />
        </Menu>
      ) : null}
    </span>
  );
}
